import React, { useState, useRef } from 'react'
import { ArticleCategory, ArticleDto, ManageArticleDto, AppLanguage, ArticleTranslationDto } from '../../../sdk'
import { apiV2 } from '../../../repositories/repository'
import MDEditor, { commands } from '@uiw/react-md-editor'
import { useDispatch } from 'react-redux'
import { setErrorMsg, setSuccessMsg } from '../../../store/Auth/action'
import useTranslate from '../../../hooks/useTranslate'
import './ArticleForm.scss'

interface ArticleFormProps {
  article?: ArticleDto
  onSave: () => void
  onCancel: () => void
}

const ArticleForm: React.FC<ArticleFormProps> = ({article, onSave, onCancel}) => {
  const dispatch = useDispatch()
  const translateLocalazy = useTranslate()
  const [loading, setLoading] = useState(false)

  const mdFileInputRef = useRef<HTMLInputElement | null>(null)
  const mdTextApiRef = useRef<any>(null)

  const [category, setCategory] = useState<ArticleCategory>(article?.category || ArticleCategory.HEALTH)
  const [titleImage, setTitleImage] = useState<string>(article?.titleImage || '')
  const [titleVideo, setTitleVideo] = useState<string | null>(article?.titleVideo || null)
  const [fullArticleUrl, setFullArticleUrl] = useState<string | null>(article?.fullArticleUrl || null)

  const [translations, setTranslations] = useState<ArticleTranslationDto[]>(article?.translations || [{
    language: AppLanguage.SV,
    title: '',
    shortDescription: '',
    content: '',
    writtenBy: ''
  }])

  const [currentLanguage, setCurrentLanguage] = useState<AppLanguage>(AppLanguage.SV)
  const currentLanguageRef = useRef<AppLanguage>(currentLanguage)
  currentLanguageRef.current = currentLanguage

  const currentTranslation = translations.find(t => t.language === currentLanguage) || {
    language: currentLanguage,
    title: '',
    shortDescription: '',
    content: '',
    writtenBy: ''
  }

  const updateTranslation = (updatedField: Partial<ArticleTranslationDto>) => {
    const lang = currentLanguageRef.current
    setTranslations(prev => {
      const existingIndex = prev.findIndex(t => t.language === lang)
      if (existingIndex >= 0) {
        const newTrans = [...prev]
        newTrans[existingIndex] = {...newTrans[existingIndex], ...updatedField}
        return newTrans
      } else {
        const fallback = {
          language: lang,
          title: '',
          shortDescription: '',
          content: '',
          writtenBy: ''
        }
        return [...prev, {...fallback, ...updatedField}]
      }
    })
  }

  const handleUploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setLoading(true)
      try {
        const file = e.target.files[0]
        const res = await apiV2.other.uploadByMedstaff({file})
        setTitleImage(res.link)
      } catch (e: any) {
        dispatch(setErrorMsg(e.body?.message || translateLocalazy('article.form.upload-error')))
      } finally {
        setLoading(false)
      }
    }
  }

  const handleUploadMarkdownImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setLoading(true)
      try {
        const file = e.target.files[0]
        const res = await apiV2.other.uploadByMedstaff({file})
        if (mdTextApiRef.current) {
          mdTextApiRef.current.replaceSelection(`![](${res.link})`)
        }
      } catch (err: any) {
        dispatch(setErrorMsg(err.body?.message || translateLocalazy('article.form.upload-error')))
      } finally {
        setLoading(false)
        if (mdFileInputRef.current) {
          mdFileInputRef.current.value = ''
        }
      }
    }
  }

  const customCommands = React.useMemo(() => {
    const customImageCommand = {
      ...commands.image,
      execute: (state: any, api: any) => {
        mdTextApiRef.current = api
        if (mdFileInputRef.current) {
          mdFileInputRef.current.click()
        }
      }
    }

    const underlineCommand = {
      name: 'underline',
      keyCommand: 'underline',
      buttonProps: {'aria-label': 'Underline text'},
      icon: (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
          <path
            d="M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6zm-7 2v2h14v-2H5z"/>
        </svg>
      ),
      execute: (state: any, api: any) => {
        let modifyText = `<u>${state.selectedText}</u>`
        if (!state.selectedText) {
          modifyText = `<u>underlined text</u>`
        }
        api.replaceSelection(modifyText)
      }
    }

    const customCmds = commands.getCommands().map(cmd => {
      if (cmd.name === 'image') {
        return customImageCommand
      }
      return cmd
    })
    customCmds.push(commands.divider, underlineCommand)
    return customCmds
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const svTranslation = translations.find(t => t.language === AppLanguage.SV)
    if (!svTranslation || !svTranslation.title || !svTranslation.shortDescription || !svTranslation.content) {
      dispatch(setErrorMsg(translateLocalazy('article.form.validation-swedish-required')))
      return
    }

    setLoading(true)

    const validTranslations = translations.filter(t => t.title || t.shortDescription || t.content)

    const payload: ManageArticleDto = {
      category,
      titleImage,
      titleVideo: titleVideo ?? undefined,
      translations: validTranslations,
      fullArticleUrl: fullArticleUrl ?? undefined,
    }

    try {
      if (article) {
        await apiV2.article.update(article._id as any, payload)
        dispatch(setSuccessMsg(translateLocalazy('article.form.update-success')))
      } else {
        await apiV2.article.create(payload)
        dispatch(setSuccessMsg(translateLocalazy('article.form.create-success')))
      }
      onSave()
    } catch (e: any) {
      dispatch(setErrorMsg(e.body?.message || translateLocalazy('article.form.save-error')))
      setLoading(false)
    }
  }

  return (
    <div className="card-box patient-info article-form">
      <div className="box-title">
        <h3>{article ? translateLocalazy('article.form.title-edit') : translateLocalazy('article.form.title-create')}</h3>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="control-holder control-holder-margin">
          <label>{translateLocalazy('article.form.label-category')}</label>
          <select value={category} onChange={e => setCategory(e.target.value as ArticleCategory)}>
            <option value={ArticleCategory.HEALTH}>{translateLocalazy('article.form.category-health')}</option>
            <option value={ArticleCategory.DISEASES}>{translateLocalazy('article.form.category-diseases')}</option>
            <option value={ArticleCategory.TREATMENT}>{translateLocalazy('article.form.category-treatment')}</option>
          </select>
        </div>

        <div className="control-holder control-holder-margin">
          <label>{translateLocalazy('article.form.label-title-image')}</label>
          {titleImage &&
              <img src={titleImage} alt="Title" className="title-image-preview"/>}
          <input type="file" accept="image/*" onChange={handleUploadImage} disabled={loading}/>
        </div>

        <div className="control-holder control-holder-margin" data-color-mode="light">
          <label>{translateLocalazy('article.form.title-video')}</label>
          <input type="text"
                 value={titleVideo ?? ''}
                 onChange={(e) => setTitleVideo(e.target.value || null)}/>
        </div>

        <div className="control-holder control-holder-margin" data-color-mode="light">
          <label>{translateLocalazy('article.form.full-article-url')}</label>
          <input type="text"
                 value={fullArticleUrl ?? ''}
                 onChange={(e) => setFullArticleUrl(e.target.value || null)}/>
        </div>

        <div className="language-versions-box">
          <div className="control-holder control-holder-margin">
            <label>{translateLocalazy('article.form.label-edit-language')}</label>
            <select value={currentLanguage} onChange={e => setCurrentLanguage(e.target.value as AppLanguage)}>
              <option value={AppLanguage.SV}>Svenska</option>
              <option value={AppLanguage.EN}>English</option>
            </select>
          </div>

          <div className="control-holder control-holder-margin">
            <label>{translateLocalazy('article.form.label-title')} ({currentLanguage.toUpperCase()}) {currentLanguage === AppLanguage.SV && '*'}</label>
            <input
              type="text"
              value={currentTranslation.title}
              onChange={e => updateTranslation({title: e.target.value})}
              placeholder={translateLocalazy('article.form.placeholder-title')}
            />
          </div>

          <div className="control-holder control-holder-margin">
            <label>{translateLocalazy('article.form.label-short-description')}
              ({currentLanguage.toUpperCase()}) {currentLanguage === AppLanguage.SV && '*'}</label>
            <textarea
              rows={3}
              value={currentTranslation.shortDescription}
              onChange={e => updateTranslation({shortDescription: e.target.value})}
              placeholder={translateLocalazy('article.form.placeholder-short-description')}
              className="full-width-textarea"
            />
          </div>

          <div className="control-holder control-holder-margin" data-color-mode="light">
            <label>{translateLocalazy('article.form.label-content')} ({currentLanguage.toUpperCase()}) {currentLanguage === AppLanguage.SV && '*'}</label>
            <input type="file" accept="image/*" ref={mdFileInputRef} className="hidden-file-input"
                   onChange={handleUploadMarkdownImage}/>
            <MDEditor
              key={currentLanguage}
              value={currentTranslation.content}
              onChange={(e) => updateTranslation({content: e || ''})}
              className="md-editor-wrapper"
              commands={customCommands}
            />
          </div>

          <div className="control-holder control-holder-margin">
            <label>{translateLocalazy('article.form.label-written-by')} ({currentLanguage.toUpperCase()})</label>
            <MDEditor
              key={currentLanguage + '_writtenBy'}
              value={currentTranslation.writtenBy || ''}
              onChange={e => updateTranslation({writtenBy: e || ''})}
              className="md-editor-wrapper"
              commands={customCommands}
            />
          </div>
        </div>

        <div className="btn-holder flex form-actions">
          <button type="button" onClick={onCancel} className="g_btn g_btn--outlined" disabled={loading}>
            {translateLocalazy('article.form.btn-cancel')}
          </button>
          <button type="submit" className="g_btn g_btn--primary" disabled={loading}>
            {loading ? translateLocalazy('article.form.btn-saving') : translateLocalazy('article.form.btn-save')}
          </button>
        </div>
      </form>
    </div>
  )
}

export default ArticleForm
